import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { HomestayCard } from "@/components/HomestayCard";
import { homestayAPI, locationAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Search, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default fallback images for locations
const defaultLocationImages = {
  "goa": "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1200&h=800&fit=crop",
  "manali": "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=1200&h=800&fit=crop",
  "jaipur": "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=1200&h=800&fit=crop",
  "kerala": "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1200&h=800&fit=crop",
  "rishikesh": "https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?w=1200&h=800&fit=crop",
  "udaipur": "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1200&h=800&fit=crop",
  "coorg": "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=800&fit=crop",
  "shimla": "https://images.unsplash.com/photo-1597074866923-dc0589150358?w=1200&h=800&fit=crop",
  "default": "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&h=800&fit=crop"
};

const LocationPage = () => {
  const { locationId } = useParams(); // This is actually the slug
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [homestays, setHomestays] = useState([]);
  const [otherLocations, setOtherLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(true);

  // Helper to get location image
  const getLocationImage = (loc) => {
    if (loc?.image) return loc.image;
    const slugLower = loc?.slug?.toLowerCase();
    return defaultLocationImages[slugLower] || defaultLocationImages.default;
  };

  useEffect(() => {
    const fetchLocationData = async () => {
      setLocationLoading(true);
      setLoading(true);
      
      try {
        // First check if there's a redirect for this slug
        try {
          const redirectCheck = await fetch(`${API_URL}/api/locations/check-redirect/${locationId}`);
          const redirectData = await redirectCheck.json();
          if (redirectData.has_redirect) {
            // Navigate to new URL
            const newSlug = redirectData.destination_url.replace('/location/', '');
            navigate(`/location/${newSlug}`, { replace: true });
            return;
          }
        } catch (redirectError) {
          // No redirect, continue with normal flow
        }
        
        // Fetch location by slug
        const locResponse = await locationAPI.getBySlug(locationId);
        setLocation(locResponse.data);
        
        // Fetch homestays for this location
        const homestaysResponse = await homestayAPI.getAll({ location_slug: locationId });
        setHomestays(homestaysResponse.data);
        
        // Fetch other locations for recommendations
        const allLocsResponse = await locationAPI.getAll();
        const others = allLocsResponse.data.filter(loc => loc.slug !== locationId);
        setOtherLocations(others);
        
      } catch (error) {
        console.error("Error fetching location data:", error);
      } finally {
        setLocationLoading(false);
        setLoading(false);
      }
    };
    
    fetchLocationData();
  }, [locationId, navigate]);

  // Generate SEO metadata (must be before any early returns)
  const currentUrl = location ? `${window.location.origin}/location/${location.slug}` : '';
  const metaTitle = location ? (location.meta_title || `Homestays in ${location.name}`) : 'Loading...';
  const metaDescription = location ? (location.meta_description || 
    (location.description?.replace(/<[^>]*>/g, '').slice(0, 155) || 
    `Find the best homestays in ${location.name}, ${location.state}. Book authentic local stays with Kokan Homestays.`)) : '';
  const ogImage = location ? (location.image?.startsWith('/') 
    ? `${API_URL}${location.image}` 
    : (location.image || `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&h=630&fit=crop`)) : '';

  // Update meta tags directly in DOM (most reliable method)
  // MUST be called before any early returns
  useEffect(() => {
    if (!location?.name) return;
    
    // Update title
    document.title = metaTitle;
    
    // Helper to update or create meta tags
    const updateMeta = (selector, content, isProperty = false) => {
      let meta = document.querySelector(selector);
      if (meta) {
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else {
          meta.name = selector.match(/name="([^"]+)"/)?.[1] || '';
        }
        meta.content = content;
        document.head.appendChild(meta);
      }
    };
    
    // Update standard meta tags
    updateMeta('meta[name="description"]', metaDescription);
    updateMeta('meta[name="robots"]', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = currentUrl;
    
    // Update Open Graph tags
    updateMeta('meta[property="og:type"]', 'website', true);
    updateMeta('meta[property="og:url"]', currentUrl, true);
    updateMeta('meta[property="og:title"]', metaTitle, true);
    updateMeta('meta[property="og:description"]', metaDescription, true);
    updateMeta('meta[property="og:image"]', ogImage, true);
    updateMeta('meta[property="og:site_name"]', 'Kokan Homestays', true);
    
    // Update Twitter Card tags
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', metaTitle);
    updateMeta('meta[name="twitter:description"]', metaDescription);
    updateMeta('meta[name="twitter:image"]', ogImage);
    
  }, [location, metaTitle, metaDescription, currentUrl, ogImage]);

  if (locationLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="location-not-found">
        <Navbar />
        <div className="pt-28 px-6 md:px-12 text-center">
          <h1
            className="text-3xl font-semibold text-foreground mb-4"
           
          >
            Location Not Found
          </h1>
          <p className="text-muted-foreground mb-6">
            The location "{locationId}" could not be found.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="location-page">
      <Navbar />
      {location && <PageSEO title={location.meta_title || `${location.name} Homestays`} description={location.meta_description || `Discover homestays and places to visit in ${location.name}, Konkan coast.`} path={`/location/${location.slug}`} image={location.cover_image} canonicalUrl={location.canonical_url} />}

      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh]" data-testid="location-hero">
        <img
          src={getLocationImage(location)}
          alt={location.name}
          className="w-full h-full object-cover"
          loading="eager"
          onError={(e) => {
            e.target.src = defaultLocationImages.default;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 bg-white/90 px-4 py-2 rounded-full hover:bg-white transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        {/* Location Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-white/80 mb-3">
              <MapPin className="w-5 h-5" />
              <span>{location.state}, {location.country || "India"}</span>
            </div>
            <h1
              className="text-4xl md:text-6xl font-medium text-white mb-4"
             
            >
              Homestays in {location.name}
            </h1>
            {location.description && location.description.replace(/<[^>]*>/g, '').trim() && (
              <p className="text-lg md:text-xl text-white/80 max-w-2xl">
                {location.description.replace(/<[^>]*>/g, '').trim()}
              </p>
            )}
            {location.homestay_count > 0 && (
              <p className="text-white/60 mt-2">
                {location.homestay_count} homestay{location.homestay_count !== 1 ? "s" : ""} available
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Listings Section */}
      <section className="py-16 px-6 md:px-12" data-testid="location-listings">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : homestays.length > 0 ? (
            <>
              <p className="text-muted-foreground mb-8">
                {homestays.length} homestay{homestays.length !== 1 ? "s" : ""} in {location.name}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {homestays.map((homestay, index) => (
                  <div
                    key={homestay.id}
                    className="animate-fade-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <HomestayCard homestay={homestay} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl shadow-soft">
              <Search className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              <h3
                className="text-2xl font-semibold text-foreground mb-3"
               
              >
                No Homestays in {location.name} Yet
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                We're working on adding more homestays in this area. Check back soon or explore other locations!
              </p>
              <Link to="/listings">
                <Button variant="outline" className="rounded-full">
                  Browse All Listings
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* SEO Content Section - Redesigned with better typography */}
      {location.seo_content && (
        <section className="py-16 px-6 md:px-12 bg-white border-t border-gray-100" data-testid="seo-content-section">
          <div className="max-w-6xl mx-auto">
            <div 
              className="seo-content-enhanced"
              dangerouslySetInnerHTML={{ __html: location.seo_content }}
            />
          </div>
          <style>{`
            .seo-content-enhanced {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              color: #374151;
              line-height: 1.8;
            }
            
            .seo-content-enhanced h1 {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              font-size: 1.75rem;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 1.5rem;
              padding-bottom: 0.75rem;
              border-bottom: 2px solid #e5e7eb;
              text-transform: uppercase;
              letter-spacing: 0.025em;
            }
            
            .seo-content-enhanced h2 {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              font-size: 1.25rem;
              font-weight: 600;
              color: #1f2937;
              margin-top: 2rem;
              margin-bottom: 1rem;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .seo-content-enhanced h3 {
              font-size: 1.125rem;
              font-weight: 600;
              color: #374151;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
            }
            
            .seo-content-enhanced p {
              margin-bottom: 1.25rem;
              text-align: justify;
              color: #4b5563;
            }
            
            .seo-content-enhanced ul,
            .seo-content-enhanced ol {
              margin-bottom: 1.5rem;
              padding-left: 1.5rem;
            }
            
            .seo-content-enhanced li {
              margin-bottom: 0.75rem;
              color: #4b5563;
            }
            
            .seo-content-enhanced ol {
              list-style-type: decimal;
            }
            
            .seo-content-enhanced ol li {
              padding-left: 0.5rem;
            }
            
            .seo-content-enhanced a {
              color: #2d5a27;
              text-decoration: none;
              font-weight: 500;
            }
            
            .seo-content-enhanced a:hover {
              text-decoration: underline;
            }
            
            .seo-content-enhanced strong {
              font-weight: 600;
              color: #1f2937;
            }
            
            .seo-content-enhanced table {
              width: 100%;
              border-collapse: collapse;
              margin: 1.5rem 0;
            }
            
            .seo-content-enhanced table th {
              text-align: left;
              padding: 0.75rem 1rem;
              font-weight: 600;
              color: #1f2937;
              border-bottom: 2px solid #e5e7eb;
              text-transform: uppercase;
              font-size: 0.875rem;
              letter-spacing: 0.05em;
            }
            
            .seo-content-enhanced table td {
              padding: 0.75rem 1rem;
              border-bottom: 1px solid #f3f4f6;
              color: #4b5563;
            }
            
            .seo-content-enhanced table tr:last-child td {
              border-bottom: none;
            }
            
            .seo-content-enhanced table td:last-child {
              text-align: right;
              font-weight: 500;
              color: #1f2937;
            }
            
            .seo-content-enhanced blockquote {
              border-left: 4px solid #2d5a27;
              padding-left: 1.5rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: #6b7280;
            }
            
            /* Two column layout for content with price list */
            @media (min-width: 768px) {
              .seo-content-enhanced .two-column,
              .seo-content-enhanced > div {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 3rem;
              }
            }
            
            /* Updated timestamp styling */
            .seo-content-enhanced .updated-date,
            .seo-content-enhanced em:last-child {
              display: block;
              margin-top: 2rem;
              font-size: 0.875rem;
              color: #9ca3af;
              font-style: italic;
            }
          `}</style>
        </section>
      )}

      {/* Other Locations */}
      {otherLocations.length > 0 && (
        <section className="py-16 px-6 md:px-12 bg-white" data-testid="other-locations">
          <div className="max-w-7xl mx-auto">
            <h2
              className="text-3xl md:text-4xl font-normal text-foreground mb-8"
             
            >
              Explore Other Destinations
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {otherLocations.slice(0, 4).map((loc) => (
                <Link
                  key={loc.id}
                  to={`/location/${loc.slug}`}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-soft"
                  data-testid={`other-location-${loc.slug}`}
                >
                  <img
                    src={getLocationImage(loc)}
                    alt={loc.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = defaultLocationImages.default;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-semibold text-white">
                      {loc.name}
                    </h3>
                    <p className="text-sm text-white/70">{loc.state}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default LocationPage;
